import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  startOnboardingTour,
  markTourComplete,
  hasTourBeenCompleted,
  resetTour as resetTourStorage,
} from "@/lib/onboardingTour";

export interface TourStep {
  id: string;
  target: string;
  title: string;
  content: string;
  placement?: "top" | "bottom" | "left" | "right";
  action?: () => void;
}

const TOUR_DISMISSED_KEY = "vexis_tour_dismissed";

export function useOnboardingTour() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [hasDismissed, setHasDismissed] = useState(false);
  const [resetCounter, setResetCounter] = useState(0);

  useEffect(() => {
    setHasCompleted(hasTourBeenCompleted());
    setHasDismissed(localStorage.getItem(TOUR_DISMISSED_KEY) === "true");
  }, []);

  const startTour = useCallback(() => {
    setResetCounter((c) => c + 1);
    setIsOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    localStorage.setItem(TOUR_DISMISSED_KEY, "true");
    setHasDismissed(true);
    setIsOpen(false);
  }, []);

  const completeTour = useCallback(() => {
    markTourComplete();
    setHasCompleted(true);
    setIsOpen(false);
  }, []);

  const resetTour = useCallback(() => {
    resetTourStorage();
    localStorage.removeItem(TOUR_DISMISSED_KEY);
    setHasCompleted(false);
    setHasDismissed(false);
  }, []);

  const shouldAutoStart = !hasCompleted && !hasDismissed;

  return {
    isOpen,
    hasCompleted,
    hasDismissed,
    shouldAutoStart,
    startTour,
    closeTour,
    completeTour,
    resetTour,
    resetCounter,
  };
}

interface OnboardingTourProps {
  steps: TourStep[];
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  resetKey?: number;
}

export function OnboardingTour({ steps: _steps, isOpen, onClose: _onClose, onComplete, resetKey }: OnboardingTourProps) {
  const startedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      startedRef.current = true;
      startOnboardingTour();
      onComplete();
    }
  }, [isOpen, resetKey]);

  return null;
}

export function TourTriggerButton({ onClick, className }: { onClick: () => void; className?: string }) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      className={cn("border-border text-muted-foreground hover:text-foreground hover:bg-muted", className)}
      data-testid="tour-trigger"
    >
      <Play className="h-4 w-4 mr-2" />
      Start Tour
    </Button>
  );
}
